import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { MarketRecap } from "../../features/kquant/types";
import { Button, Panel } from "../../components/ui/primitives";
import { MetricBadge } from "../../features/kquant/components/MetricBadge";

export function MarketRecapSection({ recap }: { recap: MarketRecap | null }) {
  const targetText = useMemo(
    () =>
      recap?.narrative ??
      "Loading…",
    [recap?.narrative]
  );

  const [phase, setPhase] = useState<"idle" | "spinning" | "streaming">("idle");
  const [streamedText, setStreamedText] = useState<string>("");

  const spinTimeoutRef = useRef<number | null>(null);
  const streamTimeoutRef = useRef<number | null>(null);

  const clearTimers = useCallback(() => {
    if (spinTimeoutRef.current != null) {
      window.clearTimeout(spinTimeoutRef.current);
      spinTimeoutRef.current = null;
    }
    if (streamTimeoutRef.current != null) {
      window.clearTimeout(streamTimeoutRef.current);
      streamTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      clearTimers();
    };
  }, [clearTimers]);

  const isGenerating = phase !== "idle";

  const onGenerate = useCallback(() => {
    if (isGenerating) return;

    clearTimers();
    setPhase("spinning");
    setStreamedText("");

    const SPIN_MS = 2000;

    spinTimeoutRef.current = window.setTimeout(() => {
      setPhase("streaming");

      let i = 0;
      const tick = () => {
        i += 1;
        setStreamedText(targetText.slice(0, i));

        if (i >= targetText.length) {
          setPhase("idle");
          return;
        }

        const ch = targetText[i - 1] ?? "";
        const delay =
          ch === "\n"
            ? 120
            : /[.!?]/.test(ch)
              ? 170
              : /[,;]/.test(ch)
                ? 120
                : 24;

        streamTimeoutRef.current = window.setTimeout(tick, delay);
      };

      streamTimeoutRef.current = window.setTimeout(tick, 80);
    }, SPIN_MS);
  }, [clearTimers, isGenerating, targetText]);

  const displayText = useMemo(() => {
    if (phase === "idle") return recap?.narrative ?? "Loading…";
    return streamedText;
  }, [phase, recap?.narrative, streamedText]);

  return (
    <section className="mt-7">
      <Panel className="p-6">
        <div className="flex items-baseline gap-4">
          <div className="text-sm font-semibold">U.S. Market Recap</div>
          <div className="text-xs text-white/55">Daily Market Trends</div>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-6 md:grid-cols-2">
          <div>
            <div className="mb-2 text-xs font-semibold text-white/65">Sector</div>
            <div className="flex flex-wrap gap-3">
              {(recap?.sector ?? []).map((s) => (
                <MetricBadge key={s.label} label={s.label} changePct={s.changePct} />
              ))}
            </div>
          </div>

          <div>
            <div className="mb-2 text-xs font-semibold text-white/65">Theme</div>
            <div className="flex flex-wrap gap-3">
              {(recap?.theme ?? []).map((t) => (
                <MetricBadge key={t.label} label={t.label} changePct={t.changePct} />
              ))}
            </div>
          </div>
        </div>

        <div className="mt-4">
          <Button
            variant="primary"
            size="sm"
            className="rounded-full"
            onClick={onGenerate}
            disabled={isGenerating}
          >
            {phase === "spinning" ? (
              <>
                <span
                  className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/25 border-t-white/80"
                  aria-hidden
                />
                Generating…
              </>
            ) : (
              "Generate U.S. Market Analysis"
            )}
          </Button>
        </div>

        <div className="mt-4 rounded-2xl border border-white/10 bg-white/5 p-5 text-xs leading-relaxed text-white/70">
          {displayText}
          {phase === "streaming" ? (
            <span className="ml-0.5 inline-block w-2 animate-pulse text-white/70">
              ▍
            </span>
          ) : null}
        </div>
      </Panel>
    </section>
  );
}
